import React, { FC, ReactNode, useContext, useEffect, useState } from 'react'
import { Modal } from '@components/Modal/Layout'
import { AiOutlineCheckCircle } from 'react-icons/ai'
import { AuthContext } from '../../../../../contexts/AuthContext'
import BidInput from '../../../../../elements/BidInput/BidInput'
import BidButton from '../../../../../elements/BidButton/BidButton'

interface Props {
  notificationType: 'SMS' | 'Email'
  children: ReactNode
}

const ConfirmationModal: FC<Props> = ({ children, notificationType }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [isConfirmed, setIsConfirmed] = useState<boolean>(false)
  const {
    instance,
    refreshUserData,
    userData: {
      profile,
      profile: { smsVerificationStatus, smsNotification },
    },
  } = useContext<any>(AuthContext)

  const onClose = () => setIsOpen(false)
  const onOpen = () => setIsOpen(true)

  useEffect(() => {
    if (!isOpen) {
      setIsConfirmed(false)
    }
  }, [isOpen, isConfirmed])

  const onConfirm = async () => {
    const url = `/notification/setup${notificationType}ByBidderId`
    const {
      data: { success },
    } = await instance.post(
      url,
      {},
      {
        params: {
          [`${notificationType.toLowerCase()}Notification`]:
            notificationType === 'Email' ? true : 1,
        },
      }
    )
    if (success) {
      refreshUserData()
      setIsOpen(false)
    }
  }

  return (
    <>
      <span onClick={onOpen} className={'w-full cursor-pointer'}>
        {children}
      </span>
      <Modal
        title={
          isConfirmed
            ? 'Success'
            : notificationType === 'SMS' && !smsVerificationStatus
            ? 'Verify Number'
            : 'Confirm'
        }
        body={
          isConfirmed ? (
            <SuccessModal onClose={onClose} />
          ) : notificationType === 'SMS' && !smsVerificationStatus ? (
            <VerifyNumber />
          ) : notificationType === 'SMS' && smsNotification === 2 ? (
            <ModalBody
              onClose={onClose}
              notificationType={notificationType}
              content={
                <h3
                  className="text-center text-md lg:text-lg leading-9 lg:leading-10 text-red mx-auto mb-2"
                  style={{ maxWidth: '58ch', lineHeight: '1.25rem' }}
                >
                  It looks like you unsubscribed from BidFTA’s SMS outbid
                  notifications. To resubscribe please text “start” to (855)
                  647-5882
                </h3>
              }
            />
          ) : (
            <ModalBody
              onClose={onClose}
              onConfirm={onConfirm}
              notificationType={notificationType}
            />
          )
        }
        isOpen={isOpen}
        onClose={onClose}
      />
    </>
  )
}

const VerifyNumber = () => {
  const {
    instance,
    refreshUserData,
    userData: {
      profile: { phone1, phone2 },
    },
  } = useContext<any>(AuthContext)

  const initVerifyNumber = async () => {
    const { data: smsRequest } = await instance.post(
      `/bidder/sendSMSToken?smsNumber=${phone1 || phone2}`
    )
  }

  useEffect(() => {
    initVerifyNumber()
  }, [])

  const [verificationCodeInput, setVerificationCodeInput] = useState<string>('')
  const [verificationError, setVerificationError] = useState<boolean>(false)
  const submitVerificationCode = async (e: any) => {
    e.preventDefault()
    try {
      const {
        data: { success },
      } = await instance.post(
        `/bidder/verifySMSToken?smsNumber=${
          phone1 || phone2
        }&token=${verificationCodeInput}`
      )
      if (success) {
        refreshUserData()
      } else {
        setVerificationError(true)
      }
    } catch (err) {
      setVerificationError(true)
    }
  }

  return (
    <div className="p-2 md:p-6 text-center">
      <form
        className="p-8 rounded-md bg-white relative"
        onSubmit={submitVerificationCode}
        role="none"
        onClick={(e: any) => e.stopPropagation()}
      >
        <h4 className="mb-6 font-bold text-xl text-left">Verify Cell Phone</h4>
        {verificationError && (
          <span className="mb-1 text-bidfta-red">
            Verification code is invalid
          </span>
        )}
        <div className="w-full mb-4">
          <BidInput
            inputValue={verificationCodeInput}
            setInputValue={setVerificationCodeInput}
            keyDown={submitVerificationCode}
            theme="blue"
            placeholder="Enter Verification Code"
            required={true}
          />
        </div>
        <BidButton
          label="Submit Code"
          theme="blue-light"
          width="full"
          type="submit"
        />
      </form>
    </div>
  )
}

const SuccessModal = ({ onClose }: any) => {
  return (
    <div className="p-2 md:p-6 text-center">
      <AiOutlineCheckCircle
        size={'5rem'}
        style={{ marginInline: 'auto' }}
        color={'green'}
      />
      <h3 className="mb-5 text-lg font-normal text-gray-500">
        You have enable SMS Notifications for your account
      </h3>
      <div className={'flex flex-row gap-4 justify-center'}>
        <button
          onClick={onClose}
          data-modal-hide="popup-modal"
          type="button"
          className="text-white bg-bidfta-blue-light hover:bg-bidfta-blue-dark focus:ring-4 focus:outline-none font-medium rounded-lg text-sm inline-flex items-center px-5 py-2.5 text-center mr-2"
        >
          Done
        </button>
      </div>
    </div>
  )
}

const ModalBody = ({
  onClose,
  onConfirm,
  notificationType,
  content,
}: {
  onClose: () => void
  onConfirm?: () => void
  notificationType: 'SMS' | 'Email'
  content?: ReactNode | string
}) => (
  <div className="p-2 md:p-6 text-center">
    <svg
      aria-hidden="true"
      className="mx-auto mb-4 text-gray-400 w-14 h-14"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
    <h3 className="mb-5 text-lg font-normal text-gray-500">
      {content ||
        `This will enable ${notificationType} Notifications for your account`}
    </h3>
    {onConfirm ? (
      <div className={'flex flex-row gap-4 justify-center'}>
        <button
          onClick={onClose}
          data-modal-hide="popup-modal"
          type="button"
          className="text-white bg-bidfta-red hover:bg-bidfta-red focus:ring-4 focus:outline-none focus:ring-red-300 font-medium rounded-lg text-sm inline-flex items-center px-5 py-2.5 text-center mr-2"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          data-modal-hide="popup-modal"
          type="button"
          className="text-white bg-bidfta-blue-light hover:bg-bidfta-blue-dark focus:ring-4 focus:outline-none font-medium rounded-lg text-sm inline-flex items-center px-5 py-2.5 text-center mr-2"
        >
          Confirm
        </button>
      </div>
    ) : (
      <button
        onClick={onClose}
        data-modal-hide="popup-modal"
        type="button"
        className="text-white bg-bidfta-red hover:bg-bidfta-red focus:ring-4 focus:outline-none focus:ring-red-300 font-medium rounded-lg text-sm inline-flex items-center px-5 py-2.5 text-center mr-2"
      >
        Close
      </button>
    )}
  </div>
)

export default ConfirmationModal
